import { API_VERSION, HOME_SERVICE, X_API_VERSION } from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_HOMES = `${HOME_SERVICE}/plans`;

export const plans = {
  getAll: async ({ filters, sort, params, signal, top, skip } = {}) => {
    const options = await apiOptions({
      url: API_HOMES,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      signal,
      params,
      filters,
      sort,
      top,
      skip,
    });
    return apiRequest(options);
  },
  getById: async ({ id, signal } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      signal,
    });
    return apiRequest(options);
  },
  getCounts: async ({ planCountType, planStatus, signal } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/counts`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
      signal,
      params: {
        planCountType,
        ...(planStatus && { planStatus }),
      },
    });
    return apiRequest(options);
  },
  create: async ({ data, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: API_HOMES,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  update: async ({ data, toastMessageConfig, signal } = {}) => {
    const options = await apiOptions({
      url: API_HOMES,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
      signal,
    });
    return apiRequest(options, toastMessageConfig);
  },
  delete: async ({ data, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${data.id}`,
      method: "delete",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  assign: async ({ id, data, signal } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
      signal,
    });
    return apiRequest(options);
  },
  updateStatus: async ({ id, data, signal } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}/status`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
      signal,
    });
    return apiRequest(options);
  },
  importPlan: async ({ id, data, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}/import-nonImported`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  importPlanReviewed: async ({ data, id, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}/check-planArea`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  importPlanOptionReviewed: async ({ data, id, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}/check-planArea-option`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  importPlanCurrentStep: async ({ data, id, toastMessageConfig } = {}) => {
    const options = await apiOptions({
      url: `${API_HOMES}/${id}/current-step`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  options: {
    getById: async ({ optionId, signal, planId } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/options/${optionId}`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        signal,
      });
      return apiRequest(options);
    },
    getAll: async ({ filters, sort, params, signal, planId } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/options`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        signal,
        params,
        filters,
        sort,
      });
      return apiRequest(options);
    },
    create: async ({ data, toastMessageConfig, signal, planId } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/options`,
        method: "post",
        headers: { [X_API_VERSION]: API_VERSION },
        data,
        signal,
      });
      return apiRequest(options, toastMessageConfig);
    },
    update: async ({ data, toastMessageConfig, signal, planId } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/options/${data.id}`,
        method: "put",
        headers: { [X_API_VERSION]: API_VERSION },
        data,
        signal,
      });
      return apiRequest(options, toastMessageConfig);
    },
    delete: async ({ id, modifiedOn, toastMessageConfig, planId } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/options/${id}`,
        method: "delete",
        headers: { [X_API_VERSION]: API_VERSION },
        data: { modifiedOn },
      });
      return apiRequest(options, toastMessageConfig);
    },
  },
  requirements: {
    getAvailable: async ({ planId, signal } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/requirements/available`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        signal,
      });
      return apiRequest(options);
    },
    getAll: async ({ planId, signal } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/requirements`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        signal,
      });
      return apiRequest(options);
    },
    update: async ({ planId, data, toastMessageConfig } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/${planId}/requirements`,
        method: "put",
        headers: { [X_API_VERSION]: API_VERSION },
        data,
      });
      return apiRequest(options, toastMessageConfig);
    },
  },
  itemTemplates: {
    getAll: async ({ filters, sort, params, signal } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/item-templates`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        signal,
        params,
        filters,
        sort,
      });
      return apiRequest(options);
    },
    create: async ({ data, toastMessageConfig }) => {
      const options = await apiOptions({
        url: `${API_HOMES}/item-templates`,
        method: "post",
        headers: { [X_API_VERSION]: API_VERSION },
        data,
      });
      return apiRequest(options, toastMessageConfig);
    },
  },
  templates: {
    getAll: async ({ sort, filters, top, skip } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/area-templates`,
        method: "get",
        headers: { [X_API_VERSION]: API_VERSION },
        filters,
        sort,
        top,
        skip,
      });
      return apiRequest(options);
    },
    createAreaTemplate: async ({ data, toastMessageConfig } = {}) => {
      const options = await apiOptions({
        url: `${API_HOMES}/area-template`,
        method: "put",
        headers: { [X_API_VERSION]: API_VERSION },
        data,
      });
      return apiRequest(options, toastMessageConfig);
    },
  },
};
